import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import { Overline, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "60-day-pilot", label: "Book a 60-day pilot" },
  { value: "technology", label: "Technology — Dignifyd Tech" },
  { value: "talent", label: "Talent & workforce — Talent Connect" },
  { value: "digital", label: "Brand & digital — Dignifyd Digital" },
  { value: "enterprise", label: "ERP & infrastructure — Enterprise Solutions" },
  { value: "data-ai", label: "Data & AI — Dignifyd Data Labs" },
  { value: "government", label: "Government / public sector mandate" },
  { value: "general", label: "General enquiry" },
];

const inputCls =
  "w-full border border-white/15 bg-ink/60 px-4 py-3.5 text-sm text-white placeholder:text-gray-600 transition-colors duration-300 focus:border-gold focus:outline-none";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    interest: "general",
    message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPilot = (e) => {
      const wanted = e.detail;
      if (INTERESTS.some((i) => i.value === wanted)) {
        setForm((f) => ({ ...f, interest: wanted }));
      } else {
        setForm((f) => ({ ...f, interest: "general" }));
      }
    };
    window.addEventListener("dignifyd:pilot", onPilot);
    return () => window.removeEventListener("dignifyd:pilot", onPilot);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — the Dignifyd team will be in touch.");
      setForm({ name: "", organisation: "", email: "", interest: "general", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please email hello@dignifyd.io or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 lg:px-8 lg:py-32">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Contact</Overline>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              Start the <span className="text-gold">conversation.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-gray-400">
              Tell us the outcome you need — a hire, a platform, a programme — and the
              right entity leads will respond. One relationship starts here.
            </p>

            <div className="mt-10 flex flex-col gap-5">
              <a
                data-testid="contact-call-button"
                href="tel:+18777350397"
                className="group flex items-center gap-4 border border-white/10 bg-surface/50 p-5 transition-colors duration-300 hover:border-gold/50"
              >
                <motion.span
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  className="inline-flex"
                >
                  <Phone className="h-5 w-5 text-gold" />
                </motion.span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
                    Toll-free — call us
                  </p>
                  <p className="mt-1 font-heading text-lg font-medium text-white transition-colors duration-300 group-hover:text-gold">
                    +1-877-735-0397
                  </p>
                </div>
              </a>
              <a
                data-testid="contact-email-link"
                href="mailto:hello@dignifyd.io"
                className="group flex items-center gap-4 border border-white/10 bg-surface/50 p-5 transition-colors duration-300 hover:border-gold/50"
              >
                <motion.span
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                  className="inline-flex"
                >
                  <Mail className="h-5 w-5 text-gold" />
                </motion.span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
                    Email
                  </p>
                  <p className="mt-1 font-heading text-lg font-medium text-white transition-colors duration-300 group-hover:text-gold">
                    hello@dignifyd.io
                  </p>
                </div>
              </a>
              <div className="flex items-center gap-4 border border-white/10 bg-surface/50 p-5">
                <motion.span
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                  className="inline-flex"
                >
                  <MapPin className="h-5 w-5 text-gold" />
                </motion.span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
                    Group headquarters
                  </p>
                  <p className="mt-1 font-heading text-lg font-medium text-white">
                    4 Winsley Street, London W1W 8HF
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form
              data-testid="enquiry-form"
              onSubmit={submit}
              className="border border-white/10 bg-surface/70 p-5 backdrop-blur-xl sm:p-8 lg:p-10"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
                Short enquiry form — lands directly with the Group team
              </p>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="enquiry-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    Your name *
                  </label>
                  <input
                    id="enquiry-name"
                    data-testid="enquiry-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-organisation" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    Organisation
                  </label>
                  <input
                    id="enquiry-organisation"
                    data-testid="enquiry-organisation-input"
                    value={form.organisation}
                    onChange={set("organisation")}
                    placeholder="Company or department"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    Work email *
                  </label>
                  <input
                    id="enquiry-email"
                    data-testid="enquiry-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@organisation.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-interest" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    I'm interested in
                  </label>
                  <select
                    id="enquiry-interest"
                    data-testid="enquiry-interest-select"
                    value={form.interest}
                    onChange={set("interest")}
                    className={`${inputCls} appearance-none`}
                  >
                    {INTERESTS.map((i) => (
                      <option key={i.value} value={i.value} className="bg-ink">
                        {i.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    What outcome do you need? *
                  </label>
                  <textarea
                    id="enquiry-message"
                    data-testid="enquiry-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="A mandate, a platform, a programme — tell us briefly."
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>
              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={sending}
                className="mt-8 flex w-full items-center justify-center gap-2 bg-gold px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-blue-300 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
